import { Box, Stack, Typography } from '@mui/material'
import { Check } from '@mui/icons-material'
import { Aurora, AuroraAssetGroup, Position } from '../types'
import { useEffect, useMemo, useRef, useState } from 'react'
import AuroraBackdrop from './AuroraBackdrop'
import { TransformComponent, TransformWrapper } from 'react-zoom-pan-pinch'
import { useTranslation } from 'react-i18next'

const MAX_ROWS = 2

const MAX_COLUMNS = 8

export interface AuroraStackPositionSelectProps {
  aurora?: Aurora
  assetGroup?: AuroraAssetGroup
  position: Position | null
  onPositionChange?: (position: Position | null) => void
  onPositionsChange?: (positions: Record<string, Position>) => void | Promise<void>
  withDragging?: boolean
}

export default function AuroraStackPositionSelect({
  aurora,
  assetGroup,
  position,
  withDragging = true,
  onPositionChange,
  onPositionsChange
}: AuroraStackPositionSelectProps) {
  if (aurora === undefined && assetGroup === undefined) {
    throw new Error('Either aurora or assetGroup must be provided.')
  }

  const { t } = useTranslation('aurora')

  const rows = Array.from({ length: MAX_ROWS })
    .map((_, i) => i)
    .reverse()

  const columns = Array.from({ length: MAX_COLUMNS }).map((_, i) => i)

  /**
   * The positions keyed by aurora id.
   */
  const [positions, setPositions] = useState<Record<string, Position>>(
    assetGroup?.customData?.positions ?? {}
  )

  const [draggedPosition, setDraggedPosition] = useState<Position | null>(null)

  const [dragOverPosition, setDragOverPosition] = useState<Position | null>(null)

  const shouldEmitPositionsChange = useRef(false)

  const occupiedPositions = useMemo(() => Object.values(positions), [positions])

  useEffect(() => {
    setPositions(assetGroup?.customData?.positions ?? {})
  }, [assetGroup?.customData?.positions])

  useEffect(() => {
    if (!withDragging) {
      setDraggedPosition(null)
      setDragOverPosition(null)
      shouldEmitPositionsChange.current = false
    }
  }, [withDragging])

  useEffect(() => {
    if (withDragging && shouldEmitPositionsChange.current) {
      shouldEmitPositionsChange.current = false
      void onPositionsChange?.(positions)
    }
  }, [onPositionsChange, positions, withDragging])

  const isPositionSelected = ({ x, y }: Position) => {
    return position?.x === x && position?.y === y
  }

  const isPositionOccupied = ({ x, y }: Position) => {
    return occupiedPositions.some((p) => p.x === x && p.y === y)
  }

  const getAuroraIdByPosition = ({ x, y }: Position): string | undefined => {
    return Object.entries(positions).find(([, p]) => p.x === x && p.y === y)?.[0]
  }

  const getAuroraByPosition = ({ x, y }: Position): Aurora | undefined => {
    const auroraId = getAuroraIdByPosition({ x, y })

    if (!auroraId) {
      return undefined
    }

    const auroraAsset = assetGroup?.assets?.find((asset) => asset?.aurora?.id === auroraId)

    return auroraAsset?.aurora
  }

  const handleDragStart = ({ x, y }: Position) => {
    if (!withDragging) {
      return
    }

    setDraggedPosition({ x, y })
  }

  const handleDragEnd = () => {
    setDraggedPosition(null)
    setDragOverPosition(null)
  }

  const handleDrop = ({ x: targetX, y: targetY }: Position) => {
    if (!withDragging) {
      return
    }

    if (draggedPosition === null) {
      return
    }

    if (isPositionSelected({ x: targetX, y: targetY })) {
      return
    }

    const sourceX = draggedPosition.x
    const sourceY = draggedPosition.y

    if (sourceX === targetX && sourceY === targetY) {
      setDraggedPosition(null)
      return
    }

    setPositions((previous) => {
      const entries = Object.entries(previous)
      const sourceEntry = entries.find(([, p]) => p.x === sourceX && p.y === sourceY)
      const targetEntry = entries.find(([, p]) => p.x === targetX && p.y === targetY)

      if (!sourceEntry) {
        return previous
      }

      const [sourceId] = sourceEntry
      const next = { ...previous }

      next[sourceId] = { x: targetX, y: targetY }

      if (targetEntry) {
        const [targetId] = targetEntry
        next[targetId] = { x: sourceX, y: sourceY }
      }

      shouldEmitPositionsChange.current = true

      return next
    })

    if (position?.x === targetX && position?.y === targetY) {
      onPositionChange?.(null)
    }

    setDraggedPosition(null)
  }

  const handlePositionChange = ({ x, y }: Position) => {
    if (isPositionSelected({ x, y })) {
      onPositionChange?.(null)
    } else {
      onPositionChange?.({ x, y })
    }
  }

  return (
    <TransformWrapper
      initialScale={1}
      minScale={1}
      maxScale={1}
      centerZoomedOut={false}
      disablePadding={true}
      limitToBounds={true}
      wheel={{ activationKeys: ['Control'] }}
      panning={{
        lockAxisY: true,
        activationKeys: withDragging ? ['Control'] : [],
        excluded: ['aurora-draggable']
      }}
    >
      <TransformComponent
        contentStyle={{ width: 'max-content', minWidth: '100%' }}
        wrapperStyle={{ width: '100%', display: 'block' }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'flex-start',
              overflowX: 'auto',
              overflowY: 'auto',
              width: '100%',
              maxHeight: {
                xs: '60vh',
                md: '70vh'
              }
            }}
          >
            <Stack direction="column" spacing={2} sx={{ minWidth: 'fit-content' }}>
              {rows.map((y) => (
                <Stack
                  direction="row"
                  spacing={2}
                  key={y}
                  sx={{ width: 'fit-content', minWidth: 'fit-content' }}
                >
                  {columns.map((x) => (
                    <Box
                      key={`${y}-${x}`}
                      onDragOver={(event) => {
                        if (
                          withDragging &&
                          draggedPosition !== null &&
                          !isPositionSelected({ x, y })
                        )
                          event.preventDefault()
                      }}
                      onDragEnter={() => {
                        if (
                          withDragging &&
                          draggedPosition !== null &&
                          !isPositionSelected({ x, y })
                        )
                          setDragOverPosition({ x, y })
                      }}
                      onDragLeave={() => {
                        if (dragOverPosition?.x === x && dragOverPosition?.y === y)
                          setDragOverPosition(null)
                      }}
                      onDrop={() => {
                        setDragOverPosition(null)
                        handleDrop({ x, y })
                      }}
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        aspectRatio: '3 / 4',
                        width: '120px',
                        flexShrink: 0,
                        position: 'relative',
                        background: (theme) =>
                          withDragging && dragOverPosition?.x === x && dragOverPosition?.y === y
                            ? theme.palette.action.focus
                            : theme.palette.background.default,
                        borderRadius: 1,
                        overflow: 'hidden'
                      }}
                    >
                      {isPositionOccupied({ x, y }) ? (
                        <Box
                          className="aurora-draggable"
                          draggable={withDragging}
                          onDragStart={() => handleDragStart({ x, y })}
                          onDragEnd={handleDragEnd}
                          sx={{
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'flex-end',
                            p: 4,
                            cursor:
                              draggedPosition?.x === x && draggedPosition?.y === y
                                ? 'grabbing'
                                : 'grab'
                          }}
                        >
                          <AuroraBackdrop aurora={getAuroraByPosition({ x, y })} />
                          <Typography fontWeight={500} sx={{ pointerEvents: 'none' }}>
                            {getAuroraIdByPosition({ x, y })}
                          </Typography>
                        </Box>
                      ) : isPositionSelected({ x, y }) ? (
                        <Box
                          sx={{
                            width: '100%',
                            height: '100%',
                            cursor: 'pointer',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'flex-end',
                            p: 4,
                            background: (theme) => theme.palette.primary.light
                          }}
                          onClick={() => handlePositionChange({ x, y })}
                        >
                          <AuroraBackdrop aurora={aurora} />
                          <Typography fontWeight={500}>{aurora?.id}</Typography>
                          <Check
                            sx={{
                              fontSize: 32,
                              position: 'absolute',
                              top: '50%',
                              left: '50%',
                              transform: 'translate(-50%, -50%)'
                            }}
                          />
                        </Box>
                      ) : (
                        <Box
                          sx={{
                            width: '100%',
                            height: '100%',
                            cursor: 'pointer',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            '&:hover': {
                              background: (theme) => theme.palette.action.hover,
                              cursor: 'pointer'
                            }
                          }}
                          onClick={() => handlePositionChange({ x, y })}
                        >
                          <Stack direction="column" sx={{ textAlign: 'center' }}>
                            <Typography color="text.secondary">{`${t('layer')}: ${y + 1}`}</Typography>
                            <Typography color="text.secondary">{`${t('slot')}: ${x + 1}`}</Typography>
                          </Stack>
                        </Box>
                      )}
                    </Box>
                  ))}
                </Stack>
              ))}
            </Stack>
          </Box>
        </Box>
      </TransformComponent>
    </TransformWrapper>
  )
}
